import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { Store, createSelector } from '@ngrx/store';
import { Observable } from 'rxjs';

import { jobInventoryFeature } from '../../../job-inventory/job-inventory.feature';

import { NavBaseComponent } from './nav-base';

interface SummaryRow {
    name: string;
    itemCount: number;
    weight: number;
}

interface InventorySummaryViewModel {
    rows: SummaryRow[];
    totalWeight: number;
}

/**
 * One row per room.
 *
 * `weight * quantity` — deliberately the same arithmetic as the editor's `currentWeight`, which does
 * `quantity * weightEach` where `weightEach` is `it.weight ?? 0`. `InventoryItem.weight` is **per
 * unit**, not a line total; the item also carries a `totalWeight`, and reaching for that instead
 * would double-count on any row the editor has written.
 *
 * **Items marked `navStatus: 'missing'` are excluded**, matching the editor. That flag is written
 * back when a scope confirm establishes an item is not going, so on a re-run this step would
 * otherwise quote a truck-load including things nobody is collecting. It is not the same as the
 * crew's in-flight review state, which lives in `navigatorData` and does not exist here.
 *
 * Rooms with no items are kept rather than filtered out: an empty room on the manifest is exactly
 * the kind of thing worth noticing before leaving the dock, and hiding it would make the room count
 * disagree with the rows.
 *
 * Extracted rather than inlined because `totalWeight` below is a second consumer — the condition the
 * NgRx conventions set for composing instead of keeping a selector inline.
 */
const selectRoomRows = createSelector(
    jobInventoryFeature.selectInventoryEditor,
    (state): SummaryRow[] => (state?.rooms ?? []).map((room) => {
        const items = (room.inventory ?? []).filter((i) => i.navStatus !== 'missing');
        return {
            name: room.name || 'Unnamed room',
            // `?? 1`, not `?? 0`, to match `_buildRoomFromStore` in the scope editor — an item
            // with no quantity counts as one unit there, and that is the value the editor
            // persists the moment the crew touches it. Defaulting to 0 here made the same item
            // read as 1 unit in the editor and 0 items / 0 lbs in this summary.
            itemCount: items.reduce((n, i) => n + (i.quantity ?? 1), 0),
            weight: items.reduce((w, i) => w + ((i.weight ?? 0) * (i.quantity ?? 1)), 0),
        };
    }),
);

const selectViewModel = createSelector({
    rows: selectRoomRows,
    totalWeight: createSelector(selectRoomRows, (rows) => rows.reduce((w, r) => w + r.weight, 0)),
});

/**
 * A read-only inventory breakdown, for the dock (**#74**).
 *
 * **Why this exists.** The crew's first sight of what they are moving is step 5, Confirm Scope —
 * which happens at the customer's house. They load the truck and drive there having seen only the
 * customer's name and two addresses. This puts the list in front of them at the dock, while the
 * answer to "is this the right truck, is this enough crew" can still change something.
 *
 * **Read-only on purpose, and that is the whole design constraint.** Editing stays on step 5. A crew
 * reviewing inventory at the dock is doing it without the customer and without the items in front of
 * them, which is worse than not reviewing it at all — the scope step exists so the two happen
 * together. So: no checkboxes, no quantity controls, no approval. Numbers and nothing else.
 *
 * Reads the same store slice the editor writes to, so it cannot drift from what step 5 will show.
 *
 * **Deliberately no weight delta**, unlike the editor's own summary bar. At this point nothing has
 * been reviewed, so current and estimated weight are the same number and a delta would read
 * `= 0 lbs` on every job — an invitation to wonder what is wrong.
 */
@Component({
    selector: 'app-nav-inventory-summary',
    standalone: true,
    imports: [CommonModule],
    styles: [`
        .inv-sum { background: #fff; border-radius: 10px; margin-bottom: 1.25rem; overflow: hidden; }

        .inv-sum-head {
            display: flex; align-items: baseline; gap: 0.5rem;
            padding: 0.85rem 1.1rem;
            background: #f4f7fa; border-bottom: 1px solid #e8edf2;
        }
        .inv-sum-title { font-size: 0.95rem; font-weight: 700; color: #1a1a1a; }
        .inv-sum-total { margin-left: auto; font-size: 0.95rem; font-weight: 700; color: #2d6a2d; }
        .inv-sum-rooms { font-size: 0.8rem; color: #667; }

        .inv-sum-row {
            display: flex; align-items: baseline; gap: 0.6rem;
            padding: 0.6rem 1.1rem;
            border-bottom: 1px solid #f0f0f0;
            font-size: 0.9rem;
        }
        .inv-sum-row:last-child { border-bottom: none; }
        .row-name { color: #222; font-weight: 500; }
        .row-items { color: #888; font-size: 0.82rem; }
        .row-weight { margin-left: auto; color: #555; white-space: nowrap; }

        .inv-sum-empty {
            padding: 1.25rem 1.1rem; text-align: center;
            color: #999; font-size: 0.88rem;
        }
    `],
    template: `
        <div class="inv-sum" *ngIf="viewModel$ | async as viewModel">
            <div class="inv-sum-head">
                <span class="inv-sum-title">{{ node.props?.title || 'What we are moving' }}</span>
                <span class="inv-sum-rooms" *ngIf="viewModel.rows.length">
                    {{ viewModel.rows.length }} {{ viewModel.rows.length === 1 ? 'room' : 'rooms' }}
                </span>
                <span class="inv-sum-total" *ngIf="viewModel.rows.length">
                    {{ viewModel.totalWeight | number:'1.0-0' }} lbs
                </span>
            </div>

            <div class="inv-sum-row" *ngFor="let r of viewModel.rows">
                <span class="row-name">{{ r.name }}</span>
                <span class="row-items">{{ r.itemCount }} {{ r.itemCount === 1 ? 'item' : 'items' }}</span>
                <span class="row-weight">{{ r.weight | number:'1.0-0' }} lbs</span>
            </div>

            <div class="inv-sum-empty" *ngIf="!viewModel.rows.length">
                No inventory recorded for this job yet.
            </div>
        </div>
    `,
})
export class NavInventorySummaryComponent extends NavBaseComponent {
    readonly viewModel$: Observable<InventorySummaryViewModel>;

    constructor(private store: Store) {
        super();
        this.viewModel$ = this.store.select(selectViewModel);
    }
}
